import { useApi } from './hooks/useApi';
import { useTeamsContext } from './hooks/useTeamsContext';
import { api } from './api/client';
import { parseAppParams } from './lib/webRoutes';
import { BootstrapWizard } from './views/BootstrapWizard';
import { TeamsQuickDial } from './views/TeamsQuickDial';
import { ErrorScreen } from './views/ErrorScreen';
import { NotInTeamsCard } from './views/NotInTeamsCard';

export default function App() {
  const teams = useTeamsContext();
  // Outside Teams there is no host to sign in with; skip /me and offer the
  // Teams deep link or the browser version instead (a dev token keeps local
  // development outside Teams working).
  const outsideTeams = teams.initialized && !teams.inTeams && !import.meta.env.VITE_DEV_TOKEN;
  const { data: me, loading, error, refetch } = useApi(
    () => (outsideTeams ? Promise.resolve(null) : api.me()),
    { deps: [outsideTeams] },
  );

  // Read view from URL (?view=admin or ?view=user) — Teams manifest passes this.
  // ?tab=callflows (the "Phone menu" static tab) and ?location=<id> deep-link
  // into the phone-menu guide.
  const params = parseAppParams(window.location.search);

  if (!teams.initialized || loading) {
    return <LoadingScreen />;
  }

  if (outsideTeams) {
    return <NotInTeamsCard />;
  }

  if (error) {
    return <ErrorScreen
      title="Couldn't load Quick Dial"
      message={error.message}
      action={{ label: 'Retry', onClick: refetch }}
    />;
  }

  if (!me) {
    return <ErrorScreen title="Authentication failed" message="Please reopen Quick Dial." />;
  }

  // Tenant hasn't completed first-run setup
  if (!me.setupComplete) {
    if (!me.canBootstrap) {
      return <ErrorScreen
        title="Quick Dial isn't set up yet"
        message="Ask a Global Administrator in your organisation to complete the first-time setup."
      />;
    }
    return <BootstrapWizard onComplete={refetch} />;
  }

  // The Admin surface is reached via the in-app User/Admin toggle (gated on
  // me.isAdmin), not its own Teams tab, so there's no admin entry point for
  // non-admins to see. A stray ?view=admin from a non-admin just lands on the
  // user view rather than an error screen.
  const initialView: 'user' | 'admin' = params.view === 'admin' && me.isAdmin ? 'admin' : 'user';

  return (
    <TeamsQuickDial
      me={me}
      initialView={initialView}
      theme={teams.theme}
      initialUserTab={params.initialUserTab}
      initialLocationId={params.initialLocationId}
      webBasePath={me.slug ? `/${me.slug}` : ''}
    />
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-500 text-sm">Loading Quick Dial…</div>
    </div>
  );
}
